import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Minus, Plus, Trash2, Send } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { OWNER } from "../data/catalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Quote() {
  const { profile, items, updateQty, removeItem, totals, clear } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    visitor_name: "", visitor_company: "", visitor_email: "", visitor_phone: "", project_details: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!items.length) return toast.error("Ajoutez au moins un service");
    if (!profile) return toast.error("Sélectionnez un profil");
    if (!form.visitor_name || !form.visitor_email || !form.visitor_phone)
      return toast.error("Nom, e-mail et téléphone requis");

    setSubmitting(true);
    try {
      const payload = {
        profile_id: profile.id,
        profile_name: profile.title,
        items: items.map(({ service_id, service_name, price_min, price_max, quantity }) => ({
          service_id, service_name, price_min, price_max, quantity,
        })),
        ...form,
      };
      const { data } = await axios.post(`${API}/quotes`, payload);
      setDone(data);
      clear();
      toast.success("Demande de devis envoyée !");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="quote-success" className="max-w-3xl mx-auto px-6 py-28 text-center">
        <div className="text-xs tracking-[0.3em] uppercase text-white/50">Référence #{done.id.slice(0, 8).toUpperCase()}</div>
        <h1 className="font-heading text-4xl sm:text-5xl mt-4 tracking-tight">Merci, votre devis est envoyé.</h1>
        <p className="text-white/60 mt-6 leading-relaxed">
          Je reviens vers vous sous 24h ouvrées à l'adresse <span className="text-white">{done.visitor_email}</span>.
          Vous pouvez aussi me joindre directement au {OWNER.phone}.
        </p>
        <div className="mt-10 inline-flex gap-4">
          <Link to="/" className="bg-white text-black px-6 py-3 text-sm tracking-wide hover:bg-white/85">Retour à l'accueil</Link>
          <button onClick={() => { setDone(null); navigate("/"); }} className="border border-white/30 px-6 py-3 text-sm tracking-wide hover:border-white">
            Nouvelle demande
          </button>
        </div>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div data-testid="quote-empty" className="max-w-3xl mx-auto px-6 py-28 text-center">
        <h1 className="font-heading text-4xl tracking-tight">Votre devis est vide</h1>
        <p className="text-white/60 mt-4">Choisissez un profil et ajoutez des services pour commencer.</p>
        <Link to="/" className="inline-block mt-8 bg-white text-black px-6 py-3 text-sm tracking-wide hover:bg-white/85">
          Voir les profils
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="quote-page" className="max-w-7xl mx-auto px-6 py-16">
      <div className="text-xs tracking-[0.3em] uppercase text-white/40">03 — Devis estimatif</div>
      <h1 className="font-heading text-4xl sm:text-5xl mt-2 tracking-tight">Votre panier</h1>
      {profile && <p className="text-white/50 mt-2">Profil · {profile.title}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-10">
        {/* Items */}
        <div className="lg:col-span-2 border border-[#1E2D4A]">
          <div className="divide-y divide-[#1E2D4A]">
            {items.map((i) => (
              <div key={i.service_id} data-testid={`quote-item-${i.service_id}`} className="p-5 flex flex-wrap gap-4 items-center">
                <div className="flex-1 min-w-[200px]">
                  <div className="font-medium">{i.service_name}</div>
                  <div className="text-xs text-white/50 mt-1">
                    {i.price_min} – {i.price_max} € TTC{i.unit ? ` · l'${i.unit}` : ""}
                  </div>
                </div>
                <div className="flex items-center border border-[#1E2D4A]">
                  <button
                    onClick={() => updateQty(i.service_id, i.quantity - 1)}
                    data-testid={`qty-minus-${i.service_id}`}
                    className="px-3 py-2 hover:bg-[#101D38]"
                  ><Minus className="w-3.5 h-3.5" /></button>
                  <span data-testid={`qty-value-${i.service_id}`} className="px-4 text-sm tabular-nums">{i.quantity}</span>
                  <button
                    onClick={() => updateQty(i.service_id, i.quantity + 1)}
                    data-testid={`qty-plus-${i.service_id}`}
                    className="px-3 py-2 hover:bg-[#101D38]"
                  ><Plus className="w-3.5 h-3.5" /></button>
                </div>
                <div className="text-right text-sm w-28 tabular-nums">
                  {i.price_min * i.quantity} – {i.price_max * i.quantity} €
                </div>
                <button
                  onClick={() => removeItem(i.service_id)}
                  data-testid={`remove-${i.service_id}`}
                  className="text-white/40 hover:text-white"
                ><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
              </div>
            ))}
          </div>
          <div className="border-t border-[#1E2D4A] p-5 flex items-center justify-between bg-[#0A1326]">
            <span className="text-sm tracking-wide uppercase text-white/60">Total estimé TTC</span>
            <span data-testid="quote-total" className="font-heading text-2xl tabular-nums">
              {totals.min} – {totals.max} €
            </span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={submit} data-testid="quote-form" className="border border-[#1E2D4A] p-6 space-y-4 bg-[#0A1326]/50">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-white/40 mb-1">Propriétaire</div>
            <div className="text-sm text-white/80">{OWNER.name}</div>
            <div className="text-xs text-white/50">{OWNER.email} · {OWNER.phone}</div>
          </div>
          <hr className="border-[#1E2D4A]" />
          <div className="text-xs tracking-[0.3em] uppercase text-white/40">Vos coordonnées</div>
          {[
            ["visitor_name", "Nom complet *", "text"],
            ["visitor_company", "Société", "text"],
            ["visitor_email", "E-mail *", "email"],
            ["visitor_phone", "Téléphone *", "tel"],
          ].map(([name, label, type]) => (
            <div key={name}>
              <label className="block text-xs text-white/60 mb-1.5">{label}</label>
              <input
                type={type}
                name={name}
                value={form[name]}
                onChange={change}
                data-testid={`input-${name}`}
                className="w-full bg-[#040914] border border-[#1E2D4A] px-3 py-2.5 text-sm focus:border-white focus:outline-none"
                required={label.includes("*")}
              />
            </div>
          ))}
          <div>
            <label className="block text-xs text-white/60 mb-1.5">Détails du projet</label>
            <textarea
              name="project_details"
              value={form.project_details}
              onChange={change}
              data-testid="input-project_details"
              rows={4}
              className="w-full bg-[#040914] border border-[#1E2D4A] px-3 py-2.5 text-sm focus:border-white focus:outline-none resize-none"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="submit-quote-btn"
            className="w-full bg-white text-black py-3 text-sm tracking-wide inline-flex items-center justify-center gap-2 hover:bg-white/85 disabled:opacity-40"
          >
            <Send className="w-4 h-4" strokeWidth={2} />
            {submitting ? "Envoi..." : "Envoyer la demande"}
          </button>
        </form>
      </div>
    </div>
  );
}
